import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import {
  mondayFirstMonthGrid,
  parseDutchDate,
  toDutchDate,
  toIsoDate,
} from "../utils/dutchDate";

/**
 * Datumveld met Nederlandse notatie en een week die op maandag begint.
 *
 * Een `<input type="date">` haalt zijn notatie en eerste weekdag uit de taal
 * van de browser, niet uit de pagina. Op een Engelstalige browser levert dat
 * `03/22/2026` op met zondag vooraan, en dat is vanuit de app niet te sturen.
 * Daarom een eigen veld: tekstinvoer als `dd-mm-jjjj` met een eigen kalender.
 *
 * De waarde blijft naar buiten toe ISO (`jjjj-mm-dd`), zodat alle bestaande
 * formulieren en API-aanroepen ongewijzigd blijven werken.
 */

const WEEKDAYS = ["ma", "di", "wo", "do", "vr", "za", "zo"];
const MONTHS = [
  "januari",
  "februari",
  "maart",
  "april",
  "mei",
  "juni",
  "juli",
  "augustus",
  "september",
  "oktober",
  "november",
  "december",
];

export default function DateField({
  value,
  onChange,
  min,
  max,
  required,
  disabled,
  id,
  name,
  ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  name?: string;
  ariaLabel?: string;
}) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const [text, setText] = useState(() => toDutchDate(value));
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(() => new Date(`${value || toIsoDate(new Date())}T12:00:00`));
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // De waarde kan ook van buitenaf wijzigen, bijvoorbeeld bij het laden van
  // een bestaande aanvraag.
  useEffect(() => {
    setText(toDutchDate(value));
    if (value) setCursor(new Date(`${value}T12:00:00`));
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const days = useMemo(() => mondayFirstMonthGrid(cursor), [cursor]);
  const outOfRange = (iso: string) =>
    Boolean((min && iso < min) || (max && iso > max));

  const commit = (raw: string) => {
    const iso = parseDutchDate(raw);
    if (iso && !outOfRange(iso)) {
      onChange(iso);
      return;
    }
    if (raw.trim() === "") {
      onChange("");
      return;
    }
    // Onleesbaar of buiten bereik: toon weer de laatst geldige waarde.
    setText(toDutchDate(value));
  };

  const pick = (day: Date) => {
    const iso = toIsoDate(day);
    if (outOfRange(iso)) return;
    onChange(iso);
    setText(toDutchDate(iso));
    setOpen(false);
  };

  const shiftMonth = (delta: number) =>
    setCursor((current) => {
      const next = new Date(current);
      next.setDate(1);
      next.setMonth(current.getMonth() + delta);
      return next;
    });

  const todayIso = toIsoDate(new Date());

  return (
    <div className="date-field" ref={wrapperRef}>
      <input
        id={fieldId}
        name={name}
        className="date-field-input"
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="dd-mm-jjjj"
        aria-label={ariaLabel}
        value={text}
        required={required}
        disabled={disabled}
        onChange={(event) => setText(event.target.value)}
        onBlur={(event) => commit(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            commit(text);
          }
        }}
      />
      <button
        type="button"
        className="date-field-toggle"
        aria-label="Kalender openen"
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
      >
        <Calendar size={16} />
      </button>
      {open && (
        <div className="date-field-calendar" role="dialog" aria-label="Kies een datum">
          <div className="date-field-header">
            <button
              type="button"
              aria-label="Vorige maand"
              onClick={() => shiftMonth(-1)}
            >
              <ChevronLeft size={16} />
            </button>
            <strong>
              {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
            </strong>
            <button
              type="button"
              aria-label="Volgende maand"
              onClick={() => shiftMonth(1)}
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="date-field-weekdays" aria-hidden="true">
            {WEEKDAYS.map((day) => (
              <span key={day}>{day}</span>
            ))}
          </div>
          <div className="date-field-days">
            {days.map((day) => {
              const iso = toIsoDate(day);
              const classes = [
                day.getMonth() === cursor.getMonth() ? "" : "is-outside",
                iso === value ? "is-selected" : "",
                iso === todayIso ? "is-today" : "",
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <button
                  type="button"
                  key={iso}
                  className={classes}
                  disabled={outOfRange(iso)}
                  aria-current={iso === value ? "date" : undefined}
                  onClick={() => pick(day)}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
          <div className="date-field-footer">
            <button type="button" onClick={() => pick(new Date())}>
              Vandaag
            </button>
            {!required && (
              <button
                type="button"
                onClick={() => {
                  onChange("");
                  setText("");
                  setOpen(false);
                }}
              >
                Wissen
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
